import React, { useMemo } from 'react';
import dynamic from 'next/dynamic';
import * as S from '@/components/Ad/SliderWithGallery/SliderWithGallery.styles';
import { SLIDER_ID } from '@/components/Ad/SliderWithGallery/SliderWithGallery.constants';
import { useBodyOverflow } from '@/hooks/useBodyOverflow';
import { useAdDetails } from '@/views/Ads/useAdDetails';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';
import { useServerDataContext } from '@/contexts';
import { Carousel } from '@/components/UI/Carousel';
import { useResponsive } from '@/hooks/useResponsive';

interface SliderWithGalleryProps {
  isGalleryOpen: boolean;
  toggleGallery: (state: boolean) => void;
  dataStat?: string;
  overlayText?: string;
  canBookmark?: boolean;
}

const Gallery = dynamic(
  () =>
    import('@/components/Ad/SliderWithGallery/Gallery').then(
      mod => mod.Gallery
    ),
  { ssr: false }
);

export const SliderWithGallery: React.FC<SliderWithGalleryProps> = ({
  isGalleryOpen,
  toggleGallery,
  dataStat,
  overlayText,
  canBookmark,
}) => {
  const { isDesktop } = useResponsive();
  const { adDetails, loading } = useAdDetails();
  const { initialAdDetails } = useServerDataContext();
  const adData = adDetails ?? initialAdDetails;
  const { title, kinds, photosPreview } = adData ?? {};

  useBodyOverflow(isGalleryOpen);

  function openGallery() {
    toggleGallery(true);
  }

  function closeGallery() {
    toggleGallery(false);
  }

  const images = useMemo(
    () => adData?.photos?.map(photo => photo.url) ?? [],
    [adData?.photos]
  );

  const sliderImages = useMemo(
    () => photosPreview?.map(photo => photo.url),
    [photosPreview]
  );

  const rawPhotos = adData?.photosPreview;
  const photos = Array.isArray(rawPhotos) ? rawPhotos : [];
  const firstSlide = photos[0]?.url ?? '';
  const isPlaceholder = firstSlide.includes('ad_i_placeholder');
  const isNoPhotos = isPlaceholder && photos.length === 1;

  function renderSkeletonLoader() {
    return isDesktop ? (
      <S.DesktopCarouselSkeletonContainer>
        <SkeletonLoader height="480px" borderRadius={14} />
        <S.DesktopThumbsSkeletonContainer>
          {Array.from({ length: 9 }, (_, index) => (
            <SkeletonLoader
              key={index}
              width={70}
              height={50}
              borderRadius={7}
            />
          ))}
        </S.DesktopThumbsSkeletonContainer>
      </S.DesktopCarouselSkeletonContainer>
    ) : (
      <SkeletonLoader height="330px" />
    );
  }

  if (!adData) {
    return renderSkeletonLoader();
  }

  if (isNoPhotos) return null;

  return (
    <React.Fragment>
      {sliderImages?.length ? (
        <Carousel
          loading={loading}
          title={title ?? ''}
          kinds={kinds}
          dataStat={dataStat ?? ''}
          sliderId={SLIDER_ID}
          overlayText={overlayText}
          openGallery={openGallery}
          images={sliderImages || []}
          loopSingleImage={sliderImages.length === 1}
        />
      ) : loading ? (
        renderSkeletonLoader()
      ) : null}

      <Gallery
        isOpen={isGalleryOpen}
        onClose={closeGallery}
        adData={adData}
        images={images}
        canBookmark={canBookmark}
      />
    </React.Fragment>
  );
};
